/**
 * 통합 페이지 네비게이션 컴포넌트 (반응형)
 * 게시판의 페이지 이동을 위한 네비게이션 UI를 제공합니다.
 * 이전/다음/마지막 페이지 이동 버튼과 페이지 번호 버튼을 표시합니다.
 * 데스크톱과 모바일 모두에서 사용 가능한 반응형 컴포넌트입니다.
 */

import styled from 'styled-components';
import preimg from '../assets/pre.png';
import nextimg from '../assets/next.png';
import lastimg from '../assets/last.png';

/**
 * PageNav 컴포넌트
 * @param {Object} props
 * @param {number} props.cnt - 전체 페이지 수
 * @param {number} props.page - 현재 페이지 번호 (1부터 시작)
 * @param {Function} props.change - 페이지 변경 핸들러 함수
 */
function PageNav({ cnt, page, change }) {
  /**
   * 페이지 네비게이션 버튼 클릭 핸들러
   * @param {Event} e - 클릭 이벤트
   */
  const handleOnClick = (e) => {
    const value = e.target.id;

    // 이전 페이지로 이동
    if (value === 'pre') {
      if (page - 1 !== 0) {
        change(page - 1);
      }
    }
    // 다음 페이지로 이동
    else if (value === 'next') {
      if (page + 1 <= cnt) {
        change(page + 1);
      }
    }
    // 마지막 페이지로 이동
    else if (value === 'last') {
      change(cnt);
    }
    // 특정 페이지 번호로 이동
    else {
      change(Number(value));
    }
  };

  /**
   * 페이지 번호 버튼들을 동적으로 생성하는 함수
   * @returns {Array} 페이지 버튼 JSX 배열
   */
  const makeButton = () => {
    const buttons = [];

    // 전체 페이지 수만큼 버튼 생성
    for (let i = 0; i < cnt; i++) {
      // 현재 페이지인 경우 활성화 표시
      const isActive = page === i + 1;

      buttons.push(
        <PageButton
          key={i}
          id={i + 1}
          onClick={handleOnClick}
          $active={isActive}
          aria-current={isActive ? 'page' : undefined}
          aria-label={`페이지 ${i + 1}`}
        >
          {i + 1}
        </PageButton>
      );
    }
    return buttons;
  };

  return (
    <Container>
      {/* 이전 페이지 버튼 */}
      <NavButton
        onClick={handleOnClick}
        disabled={page === 1}
        aria-label="이전 페이지"
      >
        <img src={preimg} alt="이전" id="pre" />
      </NavButton>

      {/* 페이지 번호 버튼들 */}
      {makeButton()}

      {/* 다음 페이지 버튼 */}
      <NavButton
        id="next"
        onClick={handleOnClick}
        disabled={page === cnt}
        aria-label="다음 페이지"
      >
        <img src={nextimg} alt="다음" id="next" />
      </NavButton>

      {/* 마지막 페이지 버튼 */}
      <NavButton
        id="last"
        onClick={handleOnClick}
        $isLast={true}
        disabled={page === cnt}
        aria-label="마지막 페이지"
      >
        <img src={lastimg} alt="마지막" id="last" />
      </NavButton>
    </Container>
  );
}

export default PageNav;

/* Styled Components */

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  text-align: center;
  box-sizing: border-box;

  /* 모바일: 7% 상단 여백, 15% 하단 여백 */
  margin-top: 7%;
  margin-bottom: 15%;

  /* 태블릿 이상: 고정 여백 */
  @media ${(props) => props.theme.media.tablet} {
    margin-top: 50px;
    margin-bottom: 50px;
  }

  * {
    box-sizing: border-box;
  }
`;

const NavButton = styled.button`
  /* 모바일: 7% 좌측 여백 */
  margin-left: 7%;

  /* 태블릿 이상: 30px 좌측 여백 */
  @media ${(props) => props.theme.media.tablet} {
    margin-left: 30px;
  }

  color: ${(props) => props.theme.colors.textTertiary};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  /* 비활성화 상태 */
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  img {
    display: block;

    /* 모바일: 작은 크기 */
    width: ${(props) => (props.$isLast ? '10.5px' : '7px')};

    /* 태블릿 이상: 원본 크기 */
    @media ${(props) => props.theme.media.tablet} {
      width: ${(props) => (props.$isLast ? '14px' : '9px')};
    }
  }

  /* 호버 효과 (터치 디바이스 제외) */
  @media (hover: hover) {
    &:hover:not(:disabled) {
      opacity: 0.7;
    }
  }

  /* 포커스 스타일 (접근성) */
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const PageButton = styled.button`
  /* 모바일: 7% 좌측 여백 */
  margin-left: 7%;

  /* 태블릿 이상: 30px 좌측 여백 */
  @media ${(props) => props.theme.media.tablet} {
    margin-left: 30px;
  }

  /* 활성화 상태에 따른 색상 */
  color: ${(props) =>
    props.$active
      ? props.theme.colors.primary
      : props.theme.colors.textTertiary};

  /* 모바일: 15px */
  font-size: 15px;

  /* 태블릿 이상: 20px */
  @media ${(props) => props.theme.media.tablet} {
    font-size: 20px;
  }

  font-family: ${(props) => props.theme.fonts.nanumSquare};
  font-weight: ${(props) => (props.$active ? 'bold' : 'normal')};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  /* 호버 효과 (터치 디바이스 제외) */
  @media (hover: hover) {
    &:hover {
      color: ${(props) => props.theme.colors.primaryHover};
    }
  }

  /* 포커스 스타일 (접근성) */
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* 활성화 상태 강조 */
  ${(props) =>
    props.$active &&
    `
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -12px;
      left: 0;
      right: 0;
      height: 2px;
      background-color: ${props.theme.colors.primary};
    }
  `}
`;
